import { Button } from '@library/components/buttons';
import { type ConfirmationModalProps } from '@library/components/modals/ConfirmationModal';
import { DateTime } from 'luxon';
import type { Status } from '@library/types/projection';
import { TeamKebabMenu } from '@library/components/menus';
import type { TradingTeamBulkActions } from '@bo-hooks/trading/useTradingTeamBulkActions';
import { TradingTeamHeaderDatePickers } from '@bo-components/trading/games/TradingTeamHeaderDatePickers';
import chevronDownIcon from '@library/assets/images/icons/chevron-down-icon.svg';
import { classNames } from '@library/utils';
import { getTooltipForStatus } from '@library/utils/status';
import { useMemo } from 'react';

export interface TradingTeamHeaderBulkActionsProps {
  boardStatus: Status;
  firstBoardTime?: string;
  firstDisplayTime?: string;
  handleTogglePlayers: () => void;
  isBulkTeamInGameEnabled: boolean;
  setConfirmationModalProps: (
    props: Omit<ConfirmationModalProps, 'onCancelModal'>
  ) => void;
  showPlayers: boolean;
  standardStartTimes: Array<string>;
  tradingTeamBulkActions: TradingTeamBulkActions;
  tradingTeamModalActions: {
    handleShowGameModes: () => void;
  };
  uniqueStartTimes: Array<string>;
  isDisplayTimeVisible?: boolean;
}

export const TradingTeamHeaderBulkActions = ({
  boardStatus,
  firstBoardTime = undefined,
  firstDisplayTime = undefined,
  handleTogglePlayers,
  isBulkTeamInGameEnabled,
  setConfirmationModalProps,
  showPlayers,
  tradingTeamBulkActions,
  tradingTeamModalActions,
  standardStartTimes,
  uniqueStartTimes,
  isDisplayTimeVisible = false
}: TradingTeamHeaderBulkActionsProps) => {
  const tradingTeamActions: Omit<TradingTeamBulkActions, 'handleDelay'> =
    useMemo(() => {
      return {
        handleChangeStatus: (status: Status) => {
          setConfirmationModalProps({
            confirmationCopy: 'Change Status',
            handleConfirm: () => {
              tradingTeamBulkActions.handleChangeStatus(status);
            },
            label: 'Changing Game Status',
            children: `Are you sure you want to change the status for all projections in this game for this league and any synced leagues to ${getTooltipForStatus(
              status
            )}?`,
            isOpen: true
          });
        },
        handleChangeBoardTime: (newBoardTime: DateTime) => {
          setConfirmationModalProps({
            confirmationCopy: 'Apply',
            handleConfirm: () => {
              tradingTeamBulkActions.handleChangeBoardTime(newBoardTime);
            },
            label: 'Change Board Time',
            children: `Are you sure you want to change the board time for all projections in this game for this league and any synced leagues to ${newBoardTime.toLocaleString(
              DateTime.DATETIME_FULL
            )}?`,
            isOpen: true
          });
        },
        handleChangeStartTime: (newStartTime: DateTime) => {
          setConfirmationModalProps({
            confirmationCopy: 'Apply',
            handleConfirm: () => {
              tradingTeamBulkActions.handleChangeStartTime(newStartTime);
            },
            label: 'Change Start Time',
            children: `Are you sure you want to change the start time of this game for this league and any synced leagues to ${newStartTime.toLocaleString(
              DateTime.DATETIME_FULL
            )}?`,
            isOpen: true
          });
        },
        handleChangeDisplayTime: (newDisplayTime: DateTime) => {
          setConfirmationModalProps({
            confirmationCopy: 'Apply',
            handleConfirm: () => {
              tradingTeamBulkActions.handleChangeDisplayTime(newDisplayTime);
            },
            label: 'Change Display Time',
            children: `Are you sure you want to change the display time of this game for this league and any synced leagues to ${newDisplayTime.toLocaleString(
              DateTime.DATETIME_FULL
            )}?`,
            isOpen: true
          });
        },
        handleChangeInGame: (inGame: boolean) => {
          setConfirmationModalProps({
            confirmationCopy: 'Change In Game',
            handleConfirm: () => {
              tradingTeamBulkActions.handleChangeInGame(inGame);
            },
            label: 'Changing In Game',
            children: `Are you sure you want to ${inGame ? 'Enable' : 'Disable'} In-game for all projections in this game for this league and any synced leagues?`,
            isOpen: true
          });
        },
        handleChangeGameModes(gameModeIds: Array<number>) {
          setConfirmationModalProps({
            confirmationCopy: 'Change Game Modes',
            handleConfirm: () => {
              tradingTeamBulkActions.handleChangeGameModes(gameModeIds);
            },
            label: 'Changing Game Modes',
            children: `Are you sure you want to change game modes for all projections in this game for this league and any synced leagues?`,
            isOpen: true
          });
        }
      };
    }, [setConfirmationModalProps, tradingTeamBulkActions]);

  return (
    <div className="mr-1 grid grid-cols-[1fr_1.5rem_1.5rem] gap-4">
      <TradingTeamHeaderDatePickers
        boardStatus={boardStatus}
        boardTimeString={firstBoardTime}
        displayTimeString={firstDisplayTime}
        standardStartTimes={standardStartTimes}
        tradingTeamActions={tradingTeamActions}
        uniqueStartTimes={uniqueStartTimes}
        isDisplayTimeVisible={isDisplayTimeVisible}
      />
      <TeamKebabMenu
        teamBulkActions={tradingTeamActions}
        teamModalActions={tradingTeamModalActions}
        isBulkTeamInGameEnabled={isBulkTeamInGameEnabled}
      />
      <Button
        className="w-6"
        type="button"
        title="ToggleGameTeamPlayersButton"
        onClick={handleTogglePlayers}
      >
        <img
          alt="Chevron Down icon"
          className={classNames(showPlayers ? 'rotate-180' : '')}
          src={chevronDownIcon}
        />
      </Button>
    </div>
  );
};
